import { HttpClient, HttpContext } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import {
  ANON_CONFIG,
  API_BASE_URL,
  APIM_SUB_KEY,
  BYPASS_AUTH_INTERCEPTOR
} from "@vp/shared/tokens";
import { finalize, map, Observable, of, shareReplay, tap } from "rxjs";

export interface AuthResponse {
  accessToken: string;
  expiresIn: number;
  metaData: unknown;
}

interface TokenCache {
  value: string;
  expiresAt: number;
}

@Injectable({
  providedIn: "root"
})
export class AnonymousAuthService {
  private tokenCache: TokenCache | null = null;
  private tokenRequest$: Observable<string> | null = null;

  private readonly anonConfig = inject(ANON_CONFIG);
  private readonly _http = inject(HttpClient);
  private readonly apiBaseUrl = inject(API_BASE_URL);
  private readonly apimSubscriptionKey = inject(APIM_SUB_KEY);

  getAnonAccessToken() {
    const now = Date.now();
    // return valid cached token
    if (this.tokenCache && this.tokenCache.expiresAt > now) {
      return of(this.tokenCache.value);
    }

    // return in-flight token
    if (this.tokenRequest$) {
      return this.tokenRequest$;
    }

    const url = this.getAnonAuthUrl();
    const authRequest = {
      origin: this.anonConfig.origin
    };
    const context = new HttpContext().set(BYPASS_AUTH_INTERCEPTOR, true);
    this.tokenRequest$ = this._http
      .post<AuthResponse>(url, authRequest, {
        context,
        headers: {
          "Ocp-Apim-Subscription-Key": this.apimSubscriptionKey
        }
      })
      .pipe(
        tap(authResponse => {
          this.tokenCache = {
            value: authResponse.accessToken,
            expiresAt: now + (authResponse.expiresIn - 120) * 1000
          };
        }),
        map(authResponse => authResponse.accessToken),
        finalize(() => {
          this.tokenRequest$ = null;
        }),
        shareReplay({ bufferSize: 1, refCount: true })
      );
    return this.tokenRequest$;
  }

  private getAnonAuthUrl() {
    const url = new URL(this.apiBaseUrl);
    const rootPath = url.pathname.split("/")[1];
    const anonBaseUrl = this.apiBaseUrl.replace(rootPath, this.anonConfig.apiSuffix);
    return `${anonBaseUrl}/auth`;
  }
}
